import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/item-types` (docs/api-design.md §4.5,
 * docs/data-model.md §3.4, docs/REQUIREMENTS.md FR-3).
 *
 * Статусное поле: fieldType='select' + config.hasCategories=true.
 *
 * Инварианты, проверяемые сервисом (не схемами):
 *  - slug уникален внутри space (item_type) / item_type (field_definition)
 *  - не более одного select-поля с hasCategories=true на ItemType
 *  - reference: config.dictionaryId обязателен и должен существовать
 *
 * Phase 8 рефакторинга связей (2026-04-29): связи между записями теперь —
 * первоклассная сущность через LinkRule
 * (`packages/shared/src/schemas/link-rules.ts`) и таблицу `item_links`.
 */
// ─── Field types ─────────────────────────────────────────────────────────────
export const FIELD_TYPES = [
    'text',
    'textarea',
    'markdown',
    'number',
    'select',
    'multi_select',
    'date',
    'reference',
    'user',
    'url',
    'boolean',
    'computed',
];
export const FieldTypeSchema = z.enum(FIELD_TYPES);
// Типы полей, реализованные в M4/M5 (остальные блокируются в сервисе)
export const SUPPORTED_FIELD_TYPES_M4 = [
    'text',
    'textarea',
    'markdown',
    'number',
    'select',
    'multi_select',
    'date',
    'reference',
    'user',
    'url',
    'boolean',
];
// Типы полей, чьи значения хранятся в одной колонке `value_text`
// (см. data-model §3.4). Смену типа между ними можно разрешить без миграции
// данных — значения остаются валидными. Все остальные переходы запрещены
// (данные лежат в других колонках / в ином формате).
export const TEXT_COMPATIBLE_FIELD_TYPES = [
    'text',
    'textarea',
    'markdown',
    'url',
];
// ─── Field config schemas (per fieldType) ────────────────────────────────────
const SelectOptionSchema = z.object({
    value: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    color: z.string().optional(),
    // category присутствует только для hasCategories=true
    category: z.enum(['backlog', 'active', 'done', 'cancelled']).optional(),
});
// Конфиг select / multi_select
export const SelectFieldConfigSchema = z.object({
    options: z.array(SelectOptionSchema).default([]),
    // true → статусное поле (категории обязательны у каждой опции)
    hasCategories: z.boolean().optional().default(false),
    // Для select — value одной опции; для multi_select — массив value
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
});
// Конфиг reference
export const ReferenceFieldConfigSchema = z.object({
    dictionaryId: z.string().uuid(),
    multiple: z.boolean().optional().default(false),
    displayColumn: z.string().optional(),
    filterByColumn: z.string().optional(),
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
    // Признак «поле-название»: reference-поле можно назначить заголовком записи.
    // В заголовке показывается не сырой entryId, а display-значение справочника
    // (резолв через resolvedRefs на фронте / JOIN на dictionary_entries в SQL).
    // Не более одного поля-названия на тип записи (инвариант обеспечивает бэкенд).
    isTitle: z.boolean().optional(),
    // ITEM-13: при создании записи автоматически подставлять текущего пользователя.
    // Работает, если ключевая колонка справочника содержит логин или email
    // пользователя (резолв на бэкенде, как в Jira-sync). Явное значение из формы
    // и автозаполнение из фильтров представления имеют приоритет.
    autofillCurrentUser: z.boolean().optional(),
});
// Конфиг user — ссылка на пользователей Lens (значение записи = id пользователя).
// В отличие от reference, источник — участники пространства, а не справочник,
// поэтому dictionaryId не нужен.
export const UserFieldConfigSchema = z.object({
    multiple: z.boolean().optional().default(false),
    defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
    // ITEM-13: при создании записи автоматически подставлять текущего пользователя.
    autofillCurrentUser: z.boolean().optional(),
});
// Конфиг number
export const NumberFieldConfigSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    precision: z.number().int().min(0).max(10).optional(),
    defaultValue: z.number().optional(),
});
// Конфиг text/textarea/markdown/url
export const TextFieldConfigSchema = z.object({
    maxLength: z.number().int().positive().optional(),
    defaultValue: z.string().optional(),
    // Признак «поле-название»: его значение выносится в заголовок записи везде —
    // карточка, грид, ссылки, канбан, поиск. Не более одного на тип записи
    // (инвариант обеспечивается бэкендом). Доступен только для текстовых полей.
    isTitle: z.boolean().optional(),
});
/**
 * Типы полей, которым можно явно назначить признак «название» (config.isTitle).
 * reference входит сюда: заголовком может быть ссылка на справочник, при этом
 * везде показывается display-значение записи справочника, а не сырой entryId.
 */
export const TITLE_CAPABLE_FIELD_TYPES = ['text', 'textarea', 'markdown', 'reference'];
/**
 * Типы полей, из которых заголовок выбирается неявно (по slug 'title'/'name'
 * или как первое поле по sortOrder). Здесь только текстовые: reference-заголовок
 * — исключительно opt-in через config.isTitle, чтобы фолбэк случайно не выбрал
 * ссылку и не показал UUID там, где нет резолва справочника.
 */
export const TITLE_FALLBACK_FIELD_TYPES = ['text', 'textarea', 'markdown'];
/**
 * Единый резолвер поля-заголовка для записи. Приоритет:
 *   1. поле с config.isTitle === true (text/textarea/markdown/reference);
 *   2. поле со slug 'title', затем 'name';
 *   3. первое текстовое поле по sortOrder.
 * Возвращает выбранное определение поля или null.
 *
 * Бэкенд использует зеркальную логику в SQL (item-meta / search) —
 * любое изменение приоритета нужно синхронизировать в обоих местах.
 */
export function resolveTitleField(fields) {
    const titleCapable = TITLE_CAPABLE_FIELD_TYPES;
    const fallbackCapable = TITLE_FALLBACK_FIELD_TYPES;
    const explicit = fields.find((f) => titleCapable.includes(f.fieldType) && f.config?.isTitle === true);
    if (explicit)
        return explicit;
    const bySlug = fields.find((f) => f.slug === 'title') ?? fields.find((f) => f.slug === 'name');
    if (bySlug)
        return bySlug;
    return (fields
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .find((f) => fallbackCapable.includes(f.fieldType)) ?? null);
}
// Конфиг date
export const DateFieldConfigSchema = z.object({
    includeTime: z.boolean().optional().default(false),
    defaultValue: z.string().optional(), // ISO 8601
});
// Конфиг boolean
export const BooleanFieldConfigSchema = z.object({
    defaultValue: z.boolean().optional(),
});
// ─── FieldDefinition DTO ─────────────────────────────────────────────────────
export const FieldDefinitionSchema = z.object({
    id: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    name: z.string().min(1).max(200),
    slug: z.string().regex(/^[a-z][a-z0-9_]{0,49}$/),
    fieldType: FieldTypeSchema,
    config: z.record(z.unknown()),
    required: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── ItemType DTO ─────────────────────────────────────────────────────────────
export const ItemTypeSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    name: z.string().min(1).max(200),
    slug: z.string().regex(/^[a-z][a-z0-9_]{0,49}$/),
    icon: z.string().nullable(),
    settings: z.record(z.unknown()),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    fields: z.array(FieldDefinitionSchema),
});
// Краткая версия для списка (без полей)
export const ItemTypeListItemSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    icon: z.string().nullable(),
    sortOrder: z.number().int(),
    fieldCount: z.number().int(),
});
// ─── POST /api/spaces/:id/item-types ────────────────────────────────────────
const SLUG_RE = /^[a-z][a-z0-9_]{0,49}$/;
export const CreateItemTypeRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    slug: z.string().trim().regex(SLUG_RE),
    icon: z.string().max(10).optional(),
});
// ─── PATCH /api/spaces/:id/item-types/:itemTypeId ───────────────────────────
export const UpdateItemTypeRequestSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    icon: z.string().max(10).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
// ─── POST /api/spaces/:id/item-types/:itemTypeId/fields ─────────────────────
export const CreateFieldRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    slug: z.string().trim().regex(SLUG_RE),
    fieldType: FieldTypeSchema,
    config: z.record(z.unknown()).optional().default({}),
    required: z.boolean().optional().default(false),
    sortOrder: z.number().int().optional(),
});
// ─── PATCH /api/spaces/:id/item-types/:itemTypeId/fields/:fieldId ───────────
export const UpdateFieldRequestSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    // Смена типа разрешена только между TEXT_COMPATIBLE_FIELD_TYPES — проверяется
    // сервисом (см. updateField), т.к. требует знания текущего типа поля.
    fieldType: FieldTypeSchema.optional(),
    config: z.record(z.unknown()).optional(),
    required: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
// ─── POST /api/spaces/:id/item-types/:itemTypeId/fields/reorder ─────────────
export const ReorderFieldsRequestSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// Legacy config schema: нужен только для разбора старых payload'ов / фикстур,
// но больше не соответствует допустимому fieldType.
export const CrossLinkFieldJiraConfigSchema = z.object({
    type: z.string().min(1).default('Relates'),
});
export const CrossLinkFieldConfigSchema = z.object({
    targetSpaceId: z.string().uuid(),
    targetItemTypeId: z.string().uuid().optional(),
    multiple: z.boolean().optional(),
    autoApply: z.boolean().optional(),
    jiraLink: CrossLinkFieldJiraConfigSchema.optional(),
});
